/**
 * Secure Cache using IndexedDB with encryption
 * - Uses a server-provided encryption key (SHA256 of userId + orgId + server secret)
 * - Uses IndexedDB for larger storage capacity
 * - Encrypts data using AES-GCM with a key derived from the server-provided key
 */

const DB_NAME = 'monada-email-cache';
const DB_VERSION = 1;
const STORE_NAME = 'encrypted-cache';

let dbPromise = null;

/**
 * Initialize IndexedDB
 */
function getDB() {
    if (dbPromise) return dbPromise;

    dbPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: 'id' });
            }
        };
    });

    return dbPromise;
}

/**
 * Generate a cache key for IndexedDB lookup
 * Note: The encryption key ensures user isolation, so the cache key only needs
 * to be unique per org/type/folder combination
 */
function generateCacheKey({ orgId, type, folder }) {
    const parts = [
        orgId || 'no-org',
        type || 'default',
        folder || 'default'
    ];
    return parts.join(':');
}

/**
 * Import the server-provided key (SHA256 hex string) as an AES-GCM encryption key
 * Uses PBKDF2 to derive the final key from the server key
 */
async function importServerKey(serverKeyHex) {
    const encoder = new TextEncoder();

    // Import the server key as key material
    const keyMaterial = await crypto.subtle.importKey(
        'raw',
        encoder.encode(serverKeyHex),
        'PBKDF2',
        false,
        ['deriveKey']
    );

    // Derive the actual encryption key using PBKDF2
    // Using a fixed salt since the server key is already unique per user/org
    const salt = encoder.encode('monada-secure-cache-v1');

    return crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt,
            iterations: 100000,
            hash: 'SHA-256'
        },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
    );
}

/**
 * Encrypt data using AES-GCM
 */
async function encryptData(key, data) {
    const encoder = new TextEncoder();
    const iv = crypto.getRandomValues(new Uint8Array(12));

    const encrypted = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        encoder.encode(JSON.stringify(data))
    );

    // Combine IV and encrypted data
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);

    // Convert to base64 for storage (chunked to avoid stack overflow on large data)
    let binary = '';
    for (let i = 0; i < combined.length; i++) {
        binary += String.fromCharCode(combined[i]);
    }
    return btoa(binary);
}

/**
 * Decrypt data using AES-GCM
 */
async function decryptData(key, encryptedString) {
    try {
        // Convert from base64
        const combined = Uint8Array.from(atob(encryptedString), c => c.charCodeAt(0));

        // Extract IV and encrypted data
        const iv = combined.slice(0, 12);
        const encrypted = combined.slice(12);

        const decrypted = await crypto.subtle.decrypt(
            { name: 'AES-GCM', iv },
            key,
            encrypted
        );

        const decoder = new TextDecoder();
        return JSON.parse(decoder.decode(decrypted));
    } catch (e) {
        console.error('[secureCache] Decryption failed:', e);
        return null;
    }
}

/**
 * Store encrypted data in IndexedDB
 * @param {string} encryptionKey - Server-provided encryption key (SHA256 hex string)
 * @param {string} orgId - Organization ID
 * @param {string} type - Cache type (e.g., 'conversations', 'folders')
 * @param {string} folder - Folder identifier
 * @param {any} data - Data to cache
 */
async function setCache({ encryptionKey, orgId, type, folder, data }) {
    if (!encryptionKey) {
        console.warn('[secureCache] No encryption key provided, skipping cache');
        return false;
    }

    try {
        const db = await getDB();
        const key = await importServerKey(encryptionKey);
        const cacheKey = generateCacheKey({ orgId, type, folder });

        const encrypted = await encryptData(key, {
            data,
            timestamp: Date.now()
        });

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);

            const request = store.put({
                id: cacheKey,
                encrypted,
                timestamp: Date.now()
            });

            request.onsuccess = () => resolve(true);
            request.onerror = () => reject(request.error);
        });
    } catch (e) {
        console.error('[secureCache] Error setting cache:', e);
        return false;
    }
}

/**
 * Retrieve and decrypt data from IndexedDB
 * @param {string} encryptionKey - Server-provided encryption key (SHA256 hex string)
 * @param {string} orgId - Organization ID
 * @param {string} type - Cache type (e.g., 'conversations', 'folders')
 * @param {string} folder - Folder identifier
 * @param {number} maxAge - Maximum cache age in milliseconds (default: 5 minutes)
 */
async function getCache({ encryptionKey, orgId, type, folder, maxAge = 5 * 60 * 1000 }) {
    if (!encryptionKey) {
        console.warn('[secureCache] No encryption key provided, skipping cache read');
        return null;
    }

    try {
        const db = await getDB();
        const key = await importServerKey(encryptionKey);
        const cacheKey = generateCacheKey({ orgId, type, folder });

        const record = await new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.get(cacheKey);

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });

        if (!record) return null;

        const decrypted = await decryptData(key, record.encrypted);
        if (!decrypted) return null;

        const age = Date.now() - decrypted.timestamp;

        return {
            data: decrypted.data,
            timestamp: decrypted.timestamp,
            age,
            isStale: age > maxAge
        };
    } catch (e) {
        console.error('[secureCache] Error getting cache:', e);
        return null;
    }
}

/**
 * Clear cache for a specific organization
 */
async function clearOrgCache({ orgId }) {
    try {
        const db = await getDB();
        const prefix = `${orgId || 'no-org'}:`;

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.openCursor();

            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    if (cursor.value.id.startsWith(prefix)) {
                        cursor.delete();
                    }
                    cursor.continue();
                } else {
                    resolve(true);
                }
            };
            request.onerror = () => reject(request.error);
        });
    } catch (e) {
        console.error('[secureCache] Error clearing cache:', e);
        return false;
    }
}

/**
 * Clear all cache (for logout or cache reset)
 */
async function clearAllCache() {
    try {
        const db = await getDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.clear();

            request.onsuccess = () => resolve(true);
            request.onerror = () => reject(request.error);
        });
    } catch (e) {
        console.error('[secureCache] Error clearing all cache:', e);
        return false;
    }
}

export default {
    setCache,
    getCache,
    clearOrgCache,
    clearAllCache,
    generateCacheKey
};
